import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  Clock,
  Calendar,
  XCircle,
  Coins,
  User,
  Star,
} from "lucide-react";
import { PACKS, STORE_LINKS } from "../../data/landing";
import { useLanguage } from "../../i18n/LanguageContext";
import { useCurrency } from "../../i18n/CurrencyContext";

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] },
  }),
};

const CurrencyToggle = () => {
  const { currency, setCurrency } = useCurrency();
  const { t } = useLanguage();
  return (
    <div
      data-testid="currency-toggle"
      role="group"
      aria-label={t.pricing.currencyLabel}
      className="relative inline-flex w-full max-w-md items-stretch rounded-full border border-[#7B1113]/15 bg-white p-1 shadow-[0_8px_24px_-12px_rgba(123,17,19,0.2)]"
    >
      <motion.span
        className="pointer-events-none absolute top-1 bottom-1 z-0 rounded-full bg-[#7B1113]"
        style={{ width: "calc(50% - 4px)" }}
        animate={{ left: currency === "inr" ? "4px" : "50%" }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
      />
      <button
        onClick={() => setCurrency("inr")}
        data-testid="currency-inr"
        aria-pressed={currency === "inr"}
        className={`relative z-10 flex-1 basis-1/2 rounded-full px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider transition-colors duration-300 sm:text-sm ${
          currency === "inr" ? "text-[#FCFAF7]" : "text-[#7B1113]"
        }`}
      >
        {t.pricing.currencies.inr}
      </button>
      <button
        onClick={() => setCurrency("usd")}
        data-testid="currency-usd"
        aria-pressed={currency === "usd"}
        className={`relative z-10 flex-1 basis-1/2 rounded-full px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider transition-colors duration-300 sm:text-sm ${
          currency === "usd" ? "text-[#FCFAF7]" : "text-[#7B1113]"
        }`}
      >
        {t.pricing.currencies.usd}
      </button>
    </div>
  );
};

const PackCard = ({ pack, index }) => {
  const { t } = useLanguage();
  const p = t.pricing;
  const highlight = pack.highlight;
  const tagText = pack.tag ? p.tags[pack.tag] : null;

  return (
    <motion.article
      custom={index}
      variants={cardVariant}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      whileHover={{ y: -8 }}
      data-testid={`pack-card-${pack.id}`}
      className={`relative flex flex-col overflow-hidden rounded-3xl transition-shadow duration-300 ${
        highlight
          ? "bg-[#7B1113] text-[#FCFAF7] shadow-[0_40px_80px_-24px_rgba(123,17,19,0.5)] lg:-mt-4 lg:mb-4"
          : "bg-white text-[#2A1112] shadow-[0_24px_48px_-12px_rgba(123,17,19,0.08)] hover:shadow-[0_32px_64px_-16px_rgba(123,17,19,0.16)]"
      }`}
    >
      {/* header stripe */}
      <div
        className={`px-6 py-3 text-center text-sm font-bold uppercase tracking-[0.14em] ${
          highlight ? "bg-[#F5A623] text-[#2A1112]" : "bg-[#F97316] text-white"
        }`}
      >
        {p.packHeaders[pack.id]}
      </div>

      <div className="flex flex-1 flex-col px-6 py-6">
        {tagText && (
          <span
            data-testid={`pack-tag-${pack.id}`}
            className={`mx-auto mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
              highlight
                ? "bg-white/15 text-[#F5A623]"
                : "bg-[#F5A623]/15 text-[#7B1113]"
            }`}
          >
            {pack.tag === "oneTimeTrial" && <Star className="h-3 w-3" />}
            {tagText}
          </span>
        )}

        <div className="text-center">
          <div
            className={`text-4xl font-bold tracking-tight ${
              highlight ? "text-[#F5A623]" : "text-[#7B1113]"
            }`}
          >
            {pack.price}
          </div>
          <div
            className={`mt-2 text-lg font-semibold ${
              highlight ? "text-[#FCFAF7]" : "text-[#2A1112]"
            }`}
          >
            {pack.coins} {p.coinsSuffix}
          </div>
        </div>

        <div
          className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${
            highlight
              ? "bg-white/12 text-[#F5A623]"
              : "bg-[#F5A623]/12 text-[#7B1113]"
          }`}
        >
          <Clock className="h-4 w-4" />
          {pack.talktimeMin} {p.talktimeSuffix}
        </div>

        <ul
          className={`mt-5 space-y-2.5 text-sm ${
            highlight ? "text-[#FCFAF7]/80" : "text-[#5E4646]"
          }`}
        >
          <li className="flex items-center gap-2">
            <XCircle className="h-4 w-4 opacity-60" />
            {p.noBonus}
          </li>
          <li className="flex items-center gap-2">
            <Calendar className="h-4 w-4 opacity-70" />
            {pack.validityDays} {p.validitySuffix}
          </li>
        </ul>

        <motion.a
          href={STORE_LINKS.googlePlay}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={p.getCoins(pack.coins)}
          data-testid={`pack-download-${pack.id}`}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          className={`mt-auto inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-[background-color,box-shadow] duration-300 ${
            highlight
              ? "mt-6 bg-[#F5A623] text-[#2A1112] shadow-[0_16px_32px_-12px_rgba(245,166,35,0.6)] hover:bg-[#f6b23f]"
              : "mt-6 bg-[#F97316] text-white shadow-[0_16px_32px_-14px_rgba(249,115,22,0.55)] hover:bg-[#ea6d15]"
          }`}
        >
          {p.getCoins(pack.coins)}
        </motion.a>
      </div>
    </motion.article>
  );
};

const CoinUsageCard = () => {
  const { t } = useLanguage();
  const u = t.pricing.usage;

  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-4 text-sm sm:text-base">
      <span className="text-[#5E4646]">{label}</span>
      <span className="font-bold text-[#F97316]">
        {value}
        <span className="ml-0.5 text-xs font-medium text-[#5E4646]">
          {u.rate.replace("2", "").trim()}
        </span>
      </span>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="mx-auto mt-14 max-w-3xl rounded-3xl bg-white p-7 shadow-[0_24px_48px_-16px_rgba(123,17,19,0.08)] md:p-10"
      data-testid="coin-usage-card"
    >
      <h3 className="text-xl font-semibold text-[#2A1112] md:text-2xl">
        {u.title}
      </h3>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:divide-x sm:divide-[#7B1113]/10">
        <div className="sm:pr-6">
          <div className="mb-4 flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FCE7F3]">
              <User className="h-4 w-4 text-[#DB2777]" />
            </span>
            <span className="text-lg font-semibold text-[#2A1112]">
              {u.saathi}
            </span>
          </div>
          <div className="space-y-2.5">
            <Row label={u.chat} value="2" />
            <Row label={u.call} value="2" />
          </div>
        </div>

        <div className="sm:pl-6">
          <div className="mb-4 flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#EDE9FE]">
              <Star className="h-4 w-4 text-[#7C3AED]" />
            </span>
            <span className="text-lg font-semibold text-[#2A1112]">
              {u.astro}
            </span>
          </div>
          <div className="space-y-2.5">
            <Row label={u.chat} value="2" />
            <Row label={u.call} value="2" />
          </div>
        </div>
      </div>

      <p className="mt-6 border-t border-[#7B1113]/8 pt-4 text-center text-xs italic text-[#5E4646] md:text-sm">
        {u.footnote}
      </p>
    </motion.div>
  );
};

export const Pricing = () => {
  const { t } = useLanguage();
  const { currency } = useCurrency();
  const p = t.pricing;
  const packs = PACKS[currency];
  const cols = packs.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";

  return (
    <section
      id="pricing"
      className="relative bg-[#F6F2EA] py-24 md:py-32"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="text-xs font-bold uppercase tracking-[0.16em] text-[#7B1113]"
          >
            {p.label}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 text-3xl font-medium leading-tight tracking-tight text-[#2A1112] md:text-4xl lg:text-5xl"
            data-testid="pricing-heading"
          >
            {p.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 text-base font-medium text-[#5E4646] md:text-lg"
          >
            {p.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 flex justify-center"
          >
            <CurrencyToggle />
          </motion.div>
        </div>

        <div
          className={`mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 ${cols} lg:gap-6`}
        >
          {packs.map((pack, i) => (
            <PackCard key={`${currency}-${pack.id}`} pack={pack} index={i} />
          ))}
        </div>

        <CoinUsageCard />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 rounded-3xl bg-white p-7 shadow-[0_24px_48px_-16px_rgba(123,17,19,0.08)] md:p-10"
          data-testid="pricing-benefits"
        >
          <h3 className="text-center text-xl font-semibold text-[#2A1112] md:text-2xl">
            {p.whySubscribe}
          </h3>
          <ul className="mt-7 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {p.benefits.map((benefit, i) => (
              <li
                key={i}
                data-testid={`benefit-${i}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#2A1112] md:text-base"
              >
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#7B1113]/10">
                  <Check className="h-3.5 w-3.5 text-[#7B1113]" strokeWidth={3} />
                </span>
                {benefit}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
